import axios from 'axios';
import { markErrorHandledByInterceptor } from './services/handled-promise-rejections.js';

export const setupInterceptors = (context) => {
  // Add response interceptor
  context.$axios.interceptors.response.use(
    function (response) {
      // Handle success response
      if (!response.data.success) {
        // Extract error message based on the different conditions
        handleErrors(context, response.data);
      }
      return response;
    },
    function (error) {
      let errorWasShown = false;

      if (axios.isCancel(error)) {
        // Cancellations are expected when the user closes a composer or removes
        // an attachment. Keep the rejection semantics without showing a false
        // connection error or reporting it as an unhandled exception.
        markErrorHandledByInterceptor(error);
      } else if (error.response?.data) {
        errorWasShown = handleErrors(context, error.response.data);
      } else if (!error.response) {
        context.$toast.show(
          'Connection lost, please retry',
          context.toasted_options.error
        );
        errorWasShown = true;
      } else {
        const errorMessage = JSON.stringify(error.response);
        console.log(`An error occurred: ${errorMessage}`);
      }

      // Expected validation failures have already been surfaced to the user.
      // Preserve other failures for global error monitoring, even if toasted.
      if (errorWasShown && error.response?.status === 422) {
        markErrorHandledByInterceptor(error);
      }
      return Promise.reject(error);
    }
  );
};

// Signup uses this to show the "enter your current password" step instead of
// treating a taken mailbox as a hard failure.
export const isEmailTakenSignupResponse = (data) => {
  if (!data) return false;
  if (data.details?.reason === 'email_taken') return true;
  const msg = `${data.error || ''} ${data.result || ''}`;
  return /already in use|already exists|select a different email/i.test(msg);
};

const handleErrors = (context, data) => {
  if (isEmailTakenSignupResponse(data)) return false;

  let customErrorMessage = '';
  let errorWasShown = false;
  if (data.result) {
    customErrorMessage = `An error occurred: ${data.result}`;
  } else if (data.error) {
    customErrorMessage = `An error occurred: ${data.error}`;
  } else if (data.error_message) {
    customErrorMessage = `An error occurred: ${data.error_message}`;
  } else if (data.errors) {
    data.errors.forEach((error) => {
      context.$toast.show(JSON.stringify(error), context.toasted_options.error);
      errorWasShown = true;
    });
  }

  if (customErrorMessage) {
    context.$toast.show(customErrorMessage, context.toasted_options.error);
    errorWasShown = true;
  }

  return errorWasShown;
};

const createAxiosInstance = (context) => {
  // Create the axios instance
  context.$axios = axios.create({
    baseURL: '',
  });

  // Setup interceptors with context
  setupInterceptors(context);

  return context.$axios;
};

export default createAxiosInstance;
